import { ref } from 'vue';
import type { RaumplanungAttributeMetadataListEntry, SpatialUnitType } from 'atlas-shared';
import type { IndicatorFilter } from './IndicatorFilter';
import { IsFavoriteFilter } from './IsFavoriteFilter';
import { TextFilter } from './TextFilter';
import { createRegionalDepthFilter } from './RegionalDepthFilter';

/**
 * Registry of all available indicator filters together with the "which filters
 * are currently active / pinned" selection logic.
 *
 * This registry maintains a **dynamic, reactive list** of active filter
 * instances plus a per-filter pinned state. Each leaf node of the filter tree
 * defines its own (tag) filter instance that is registered here at runtime via
 * {@link registerFilter}. A filter becomes active by being selected (focused)
 * or by being pinned.
 *
 * ### Activity semantics
 * The registry is the **single source of truth** for whether a filter is
 * active: a filter is active if it is focused or pinned (see
 * {@link getActiveFilterIds}). Concrete filters therefore do **not** carry
 * their own active flag; their `applies` is only invoked for filters the
 * registry considers active. For filters with additional configurable state
 * (e.g. the text query), {@link applyConfig} still delegates to `setConfig`.
 *
 * ### Combination semantics
 * All active filters — focused **and** pinned — are combined with a plain
 * logical AND via {@link getEffectivePredicate}: a dataset is shown only if it
 * passes **every** active filter.
 *
 * The currently active/pinned filter state is held in Vue `ref`s so that
 * consuming computed properties re-evaluate when it changes.
 */
export class IndicatorFilterRegistry {

    private filters: Array<IndicatorFilter<any>>

    /** The "focused" active filter id (the one last selected from the tree rows). */
    private focusedFilterId = ref<string | null>(null)

    /** Ids of filters that are pinned (kept active even when the focus moves). */
    private pinnedFilterIds = ref<Array<string>>([])

    constructor(filters: Array<IndicatorFilter<any>> = []) {
        this.filters = [...filters]
    }

    //#region Registration

    /** All known filter instances. */
    getFilters(): Array<IndicatorFilter<any>> {
        return this.filters
    }

    /**
     * Registers a new filter instance (e.g. one per tree leaf).
     * Idempotent: no-op if a filter with the same id is already known.
     */
    registerFilter(filter: IndicatorFilter<any>): void {
        if (!this.filters.some(f => f.id === filter.id)) {
            this.filters = [...this.filters, filter]
        }
    }

    /** Removes a filter instance from the registry (e.g. on tree reload). */
    unregisterFilter(id: string): void {
        this.filters = this.filters.filter(f => f.id !== id)
        if (this.focusedFilterId.value === id) {
            this.focusedFilterId.value = null
        }
        this.pinnedFilterIds.value = this.pinnedFilterIds.value.filter(p => p !== id)
    }

    /** Returns the filter with the given id, or null. */
    getFilter(id: string): IndicatorFilter<any> | null {
        return this.filters.find(f => f.id === id) ?? null
    }

    //#endregion Registration

    //#region Focus / active

    /**
     * Makes the filter with `id` the focused active filter. Selecting a leaf
     * overrides the previous focus but **keeps** all currently pinned filters
     * active. Pass `null` to clear the focus ("show all"); pinned filters stay.
     */
    setActiveFilter(id: string | null): void {
        if (this.focusedFilterId.value !== null && this.focusedFilterId.value !== id) {
            // Do not reset filters that remain active through pinning.
            if (!this.isPinned(this.focusedFilterId.value)) {
                this.getFilter(this.focusedFilterId.value)?.reset()
            }
        }
        this.focusedFilterId.value = id
    }

    /** The currently focused filter, or null. */
    getFocusedFilter(): IndicatorFilter<any> | null {
        return this.getFilter(this.focusedFilterId.value ?? '')
    }

    /**
     * Activates a filter that has no own configurable state (just focuses it).
     * Used for per-leaf filters whose only "configuration" is their identity.
     */
    activate(id: string): void {
        this.setActiveFilter(id)
    }

    /**
     * Applies configuration to filter `id` and focuses it. Used when a concrete
     * filter's UI emits a configuration change (text input, favorites toggle).
     */
    applyConfig<Config>(id: string, config: Config): void {
        const filter = this.getFilter(id)
        if (!filter) return
        filter.setConfig(config as any)
        this.setActiveFilter(id)
    }

    //#endregion Focus / active

    //#region Pinning

    /**
     * Pins the filter with `id`: it gains/keeps the active state and stays in
     * the active set even when another filter is focused. No-op if unknown.
     *
     * If the filter belongs to an exclusive group (see
     * {@link IndicatorFilter.exclusiveGroup}), any other currently pinned
     * filter of the same group is unpinned first, so only one filter of the
     * group can be pinned at any given moment.
     */
    pin(id: string): void {
        const filter = this.getFilter(id)
        if (!filter) return
        if (!this.pinnedFilterIds.value.includes(id)) {
            // Enforce exclusivity: unpin any other pinned filter of the same group.
            if (filter.exclusiveGroup) {
                for (const otherId of [...this.pinnedFilterIds.value]) {
                    const other = this.getFilter(otherId)
                    if (other && other.exclusiveGroup === filter.exclusiveGroup) {
                        this.unpin(otherId)
                    }
                }
            }
            this.pinnedFilterIds.value = [...this.pinnedFilterIds.value, id]
        }
    }

    /**
     * Removes the pin from `id` and fully deactivates the filter: it is also
     * removed from the focus (if focused) and reset, so it no longer restricts
     * the list.
     */
    unpin(id: string): void {
        this.pinnedFilterIds.value = this.pinnedFilterIds.value.filter(p => p !== id)
        if (this.focusedFilterId.value === id) {
            this.focusedFilterId.value = null
        }
        this.getFilter(id)?.reset()
    }

    /** Whether the filter with `id` is pinned. */
    isPinned(id: string): boolean {
        return this.pinnedFilterIds.value.includes(id)
    }

    /** Ids of all currently pinned filters. */
    getPinnedIds(): Array<string> {
        return [...this.pinnedFilterIds.value]
    }

    //#endregion Pinning

    //#region Effective filtering

    /**
     * The ids of all filters that are currently effective (focused OR pinned),
     * deduplicated. Reactive.
     */
    getActiveFilterIds(): Array<string> {
        const ids = new Set<string>()
        if (this.focusedFilterId.value !== null) ids.add(this.focusedFilterId.value)
        for (const id of this.pinnedFilterIds.value) ids.add(id)
        return [...ids]
    }

    /** All currently effective active filter instances. */
    getActiveFilters(): Array<IndicatorFilter<any>> {
        return this.getActiveFilterIds()
            .map(id => this.getFilter(id))
            .filter((f): f is IndicatorFilter<any> => f !== null)
    }

    /**
     * Returns whether `dataset` passes the effective filters. All currently
     * active filters (focused + pinned) are combined with a plain logical AND:
     * a dataset is shown only if it passes **every** active filter. If no
     * filter is active, every dataset matches.
     */
    getEffectivePredicate(dataset: RaumplanungAttributeMetadataListEntry): boolean {
        const active = this.getActiveFilters()
        if (active.length === 0) return true
        return active.every(f => f.applies(dataset))
    }

    /**
     * Removes **all** active filters and pins: clears the focus, unpins every
     * pinned filter and resets every currently active filter instance. Used by
     * the "Alle Indikatoren" (show all) action.
     */
    clear(): void {
        for (const f of this.getActiveFilters()) {
            f.reset()
        }
        this.focusedFilterId.value = null
        this.pinnedFilterIds.value = []
    }

    //#endregion Effective filtering
}

/** The administrative levels offered as leaf filters under the "Administrative Ebene" node. */
export const ADMINISTRATIVE_LEVELS: Array<SpatialUnitType> = [
    "Gemeinde",
    "Kreis",
    "Verwaltungsgemeinschaft",
    "Metropolregion",
]

/** The exclusive group shared by all administrative-level filters. */
export const ADMINISTRATIVE_LEVEL_EXCLUSIVE_GROUP = 'administrativeLevel'

/** Creates a fresh registry pre-populated with the shared non-leaf filters. */
export function createIndicatorFilterRegistry(): IndicatorFilterRegistry {
    return new IndicatorFilterRegistry([
        new IsFavoriteFilter(),
        new TextFilter(),
        ...ADMINISTRATIVE_LEVELS.map(level => createRegionalDepthFilter(level, ADMINISTRATIVE_LEVEL_EXCLUSIVE_GROUP)),
    ])
}